import { ChangeDetectionStrategy, Component, Inject, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { Catalog, DataProduct } from '@mtna/pojo-consumer-ui';

/**
 * Data to initialize the RdsDataProductSelectionDialogComponent
 *
 * @author Will Davis <will.davis@mtna.us>
 */
export class RdsDataProductSelectionDialogData {
  /**
   * Creates an instance of RdsDataProductSelectionDialogData.
   * @param rootCatalog root level catalog to make a selection from
   * @param title title for the dialog, defaults to: "RDS Configuration"
   * @param description description for the dialog, defaults to: "First choose a catalog, then pick a data product to work with."
   * @param selectedCatalogId optional selected catalog ID
   * @param selectedDataProductId optional selected data product ID
   * @param forceSelection Whether to require the user to make a selection, defaults to false
   * @param dialogContentHeight dialog content height, defaults to 60vh
   */
  constructor(
    public rootCatalog: Catalog,
    public title: string = 'RDS Configuration',
    public description: string = 'First choose a catalog, then pick a data product to work with.',
    public selectedCatalogId?: string,
    public selectedDataProductId?: string,
    public forceSelection: boolean = false,
    public dialogContentHeight: string = '50vh'
  ) {}
}

/**
 * Result of the RdsDataProductSelectionDialogComponent on close.
 * Describes the selection made.
 *
 * @author Will Davis <will.davis@mtna.us>
 */
export class RdsDataProductSelectionDialogOutput {
  /**
   * Creates an instance of RdsDataProductSelectionDialogOutput.
   * @param catalogId selected catalog ID
   * @param dataProductId selected data product ID
   */
  constructor(public catalogId: string, public dataProductId: string) {}
}

/**
 * Dialog to select a catalog and data product for use in an RDS environment.
 *
 * @author Will Davis <will.davis@mtna.us
 */
@Component({
  selector: 'rds-data-product-selection-dialog',
  templateUrl: './data-product-selection-dialog.component.html',
  styles: [
    `
      /* Prevent horizontal scroll when first catalog selection slides in */
      rds-data-product-selection-dialog .mat-dialog-content {
        overflow-x: hidden;
      }
    `,
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RdsDataProductSelectionDialogComponent {
  /** Dialog result, constructs a RdsDataProductSelectionDialogOutput.*/
  get output(): RdsDataProductSelectionDialogOutput {
    return !!this.selectedCatalog && !!this.selectedDataProduct
      ? new RdsDataProductSelectionDialogOutput(this.selectedCatalog.id, this.selectedDataProduct.id)
      : null;
  }

  /** The catalog selected by the user */
  selectedCatalog: Catalog;

  /** The data product selected by the user */
  selectedDataProduct: DataProduct;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: RdsDataProductSelectionDialogData,
    public dialogRef: MatDialogRef<RdsDataProductSelectionDialogComponent, RdsDataProductSelectionDialogOutput>
  ) {
    // If a selected catalog was injected, find it and set it
    this.selectedCatalog = this.data.rootCatalog.catalogs.find((c) => c.id === data.selectedCatalogId);
    // If a selected data product was injected, find it and set it
    this.selectedDataProduct =
      !!this.selectedCatalog && !!this.selectedCatalog.dataProducts
        ? this.selectedCatalog.dataProducts.find((dp) => dp.id === data.selectedDataProductId)
        : null;
  }
}
